import React, { useState } from 'react';
import './AllokerOppgaveModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

function AllokerOppgaveModal({ oppgave, onClose, onAlloker }) {
  const [formData, setFormData] = useState({
    dato: getTodayDate(),
    timer: oppgave?.estimert_timer || 1
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState(null);

  // Hjelper for å få dagens dato i YYYY-MM-DD format
  function getTodayDate() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'timer' ? parseFloat(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWarning(null);

    try {
      const token = localStorage.getItem('access_token');

      // Konverter dato til Unix timestamp (midnatt norsk tid)
      const datoObj = new Date(formData.dato + 'T00:00:00');
      const datoTimestamp = Math.floor(datoObj.getTime() / 1000);

      const response = await fetch(`${API_URL}/capacity/manual-allocate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oppgave_id: oppgave.id,
          dato: datoTimestamp,
          timer: formData.timer
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || `HTTP error ${response.status}`);
        return;
      }

      const data = await response.json();

      if (data.success) {
        // Hvis det er overbooking, vis advarsel
        if (data.overbooked) {
          setWarning({
            message: data.warning,
            utnyttelsesgrad: data.utnyttelsesgrad,
            total_arbeidstimer: data.total_arbeidstimer,
            totalt_allokert: data.totalt_allokert,
            eksisterende_oppgaver: data.eksisterende_oppgaver || []
          });
          // Vent med å lukke, vis advarsel først
          setTimeout(() => {
            onAlloker({
              ...oppgave,
              allokert_dato: formData.dato,
              allokerte_timer: formData.timer
            });
            onClose();
          }, 4000); // Vis advarsel i 4 sekunder
        } else {
          // Normal allokering, lukk med en gang
          onAlloker({
            ...oppgave,
            allokert_dato: formData.dato,
            allokerte_timer: formData.timer
          });
          onClose();
        }
      } else {
        setError(data.error || 'Kunne ikke allokere oppgave');
      }
    } catch (err) {
      console.error('Feil ved allokering:', err);
      setError('Nettverksfeil ved allokering');
    } finally {
      setLoading(false);
    }
  };

  if (!oppgave) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content allocate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Alloker oppgave</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="oppgave-info">
            <h3>{oppgave.tittel}</h3>
            {oppgave.beskrivelse && (
              <p className="oppgave-beskrivelse">{oppgave.beskrivelse}</p>
            )}
            <div className="oppgave-meta">
              <span className={`prioritet-badge prioritet-${oppgave.prioritet}`}>
                {oppgave.prioritet}
              </span>
              {oppgave.forfall_dato && !isNaN(oppgave.forfall_dato) && (
                <span className="forfall-info">
                  📅 Frist: {new Date(oppgave.forfall_dato * 1000).toLocaleDateString('nb-NO')}
                </span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="dato">Velg dato *</label>
            <input
              type="date"
              id="dato"
              name="dato"
              value={formData.dato}
              onChange={handleChange}
              required
            />
            <small className="form-help">
              Oppgaven vises i kapasitetskalenderen hvis datoen er innenfor 14 dager
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="timer">Antall timer *</label>
            <input
              type="number"
              id="timer"
              name="timer"
              value={formData.timer}
              onChange={handleChange}
              min="0.25"
              max="24"
              step="0.25"
              required
            />
            <small className="form-help">
              Estimert tid: {oppgave.estimert_timer || 'Ikke satt'} timer
            </small>
          </div>

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {warning && (
            <div className="warning-message">
              <h4>⚠️ {warning.message}</h4>
              <p>
                Utnyttelsesgrad: <strong>{warning.utnyttelsesgrad}%</strong>
                ({warning.totalt_allokert}t / {warning.total_arbeidstimer}t)
              </p>

              {warning.eksisterende_oppgaver.length > 0 && (
                <div className="eksisterende-oppgaver">
                  <p><strong>Allerede allokert denne dagen:</strong></p>
                  <ul>
                    {warning.eksisterende_oppgaver.map((opp, idx) => (
                      <li key={idx}>
                        {opp.tittel} ({opp.allokerte_timer}t)
                        <span className={`prioritet-badge prioritet-${opp.prioritet}`}>
                          {opp.prioritet}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="suggestion">💡 Vurder å flytte lavere prioriterte oppgaver</p>
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Avbryt
            </button>

            <button
              type="submit"
              className="btn-allocate"
              disabled={loading}
            >
              {loading ? 'Allokerer...' : 'Alloker oppgave'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AllokerOppgaveModal;
